import type { Field } from 'models';
import type { RootState } from 'reducers/types';
import { getFieldById } from 'selectors';

const populatedFields = (state: RootState, fields: string[]) =>
  fields.map(id => getFieldById(state, id));

const isSectionPrefilled = (fields: Field[]) => {
  const requiredFields = fields.filter(field => field.required);

  if (requiredFields.length === 0) {
    return true;
  }
  return requiredFields.every(field => field.apiReadOnly || field.prefilled);
};

export const getSectionsByEntity = (
  state: RootState,
  entity: 'offers' | 'recipient' | 'sender',
) => {
  const {
    entities: {
      sections: {
        [entity]: { byId: sectionsById, ids: sectionIds },
      },
    },
  } = state;

  return sectionIds.map(id => {
    const section = { ...sectionsById[id] };
    const fields = populatedFields(state, section.fields as string[]);
    const prefilled = isSectionPrefilled(fields);

    return { ...section, fields, prefilled };
  });
};

const mergeDuplicatedSections = sections => {
  return sections.reduce((previousSections, section) => {
    const repeatedSection = previousSection =>
      previousSection.name === section.name;
    const previousSection = previousSections.find(repeatedSection);

    if (previousSection) {
      previousSection.fields = [...previousSection.fields, ...section.fields];
      previousSection.prefilled = isSectionPrefilled(section.fields);
    } else {
      previousSections.push(section);
    }

    return previousSections;
  }, []);
};

export const getSectionsByEntities = (state, entities) => {
  const sections = entities.reduce((accumulator, entity) => {
    const sections = getSectionsByEntity(state, entity);
    return accumulator.concat(sections);
  }, []);

  return mergeDuplicatedSections(sections);
};

export const getSenderConfirmationDetails = state => {
  const senderSections = getSectionsByEntity(state, 'sender');
  const offerSections = getSectionsByEntity(state, 'offers');

  senderSections.forEach(senderSection => {
    const foundSection = offerSections.find(offerSection =>
      offerSection.id.includes(senderSection.id),
    );

    if (!foundSection) {
      return false;
    }

    const offerFields = foundSection.fields;
    senderSection.fields = senderSection.fields.concat(offerFields);
  });

  return senderSections;
};
